import { ChevronDownIcon, DocumentIcon, ProfileIcon } from '../../../../../../../assets/icons';
import { selectSideBarExpanded } from '../../../../../../../store/workspace/selectors';
import { examStatusTitle } from '../../../../../../../utils/generalHelpers';
import { useDispatch, useSelector } from '../../../../../../../store/store';
import CheckBox from '../../../../../../../components/CheckBox';
import { ForwardedRef, forwardRef, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import ResultIcon from '../../../../components/ResultIcon';
import { RECRUITING_INTERVIEW_CRITERIA } from '../../../../constants';
import { useApplicantSelection } from '../../../../hooks/useApplicantSelection';
import { useExpandableRows } from '../../../../hooks/useExpandableRows';
import { useExaminerProfile } from '../../../../hooks/useExaminerProfile';
import styles from './style.module.scss';
import {
    EvaluatedCriteria,
    RecruitingInterviewStatics,
    RecruitingInterviewExaminerData,
} from '../../../../../../../store/workspace/types';
import {
    setSelectedInterviewApplicant,
    setRecruitingInterviewReportModal,
} from '../../../../../../../store/workspace/slice';

const COLUMN_COUNT = 12;

interface Props {
    selectable: boolean;
    jobId: string | undefined;
    statics: RecruitingInterviewStatics | null;
};

const RecruitingInterviewTable = forwardRef((props: Props, ref: ForwardedRef<HTMLDivElement>) => {
    const { jobId, statics, selectable } = props;

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isSidebarExpanded = useSelector(selectSideBarExpanded);
    const openExaminerProfile = useExaminerProfile(jobId);
    const { isExpanded, toggleRow } = useExpandableRows<string>();

    const applicants = statics?.examiners ?? [];
    const { isSelected, toggleApplicant, toggleAll, allSelected } = useApplicantSelection(applicants);

    const openReport = (criteria: EvaluatedCriteria) => {
        dispatch(setSelectedInterviewApplicant(criteria));
        dispatch(setRecruitingInterviewReportModal(true));
    };

    const navigateToResults = (applicant: RecruitingInterviewExaminerData) => {
        if (!applicant.answerAnalysis) return;
        navigate(`${applicant.userId}`);
    };

    const criteriaOf = (applicant: RecruitingInterviewExaminerData, key: string) =>
        applicant.evaluatedCriteria?.find(item => item.nameKey === key);

    return (
        <div className={styles.wrapper}>
            <div className={styles.table} ref={ref}>
                <table>
                    <thead>
                        {selectable && <tr>
                            <th colSpan={COLUMN_COUNT}>
                                <div className={styles.select_all}>
                                    <CheckBox onClick={toggleAll} checked={allSelected} />
                                    <span>Select All ({applicants.length})</span>
                                </div>
                            </th>
                        </tr>}
                        <tr data-highlight='true'>
                            <th>#</th>
                            <th>Name</th>
                            {!isSidebarExpanded && <th>Status</th>}
                            {RECRUITING_INTERVIEW_CRITERIA.map(criteria => <th key={criteria.key}>
                                {criteria.head.map((line, index) => <Fragment key={line}>
                                    {index > 0 && <br />}{line}
                                </Fragment>)}
                            </th>)}
                            <th data-noprint='true'>Questions<br />& feedbacks</th>
                            <th data-noprint='true'>Info</th>
                        </tr>
                    </thead>
                    <tbody>
                        {applicants.map((applicant, index) => <tr key={applicant.userId}>
                            <td>{index + 1}</td>
                            <td>
                                <div className={styles.examinerName}>
                                    {selectable && <CheckBox
                                        onClick={() => toggleApplicant(applicant.userId)}
                                        checked={isSelected(applicant.userId)}
                                    />}
                                    <span>{applicant.first_name} {applicant.last_name}</span>
                                </div>
                            </td>
                            {!isSidebarExpanded && <td>
                                <span className={styles.status} data-status={examStatusTitle(applicant.status)}>
                                    {examStatusTitle(applicant.status)}
                                </span>
                            </td>}
                            {RECRUITING_INTERVIEW_CRITERIA.map(criteria => {
                                const matched = criteriaOf(applicant, criteria.key);

                                return <td key={criteria.key}>
                                    {matched ? <div className={styles.group} onClick={() => openReport(matched)}>
                                        <ResultIcon result={matched.result} />
                                        <span>Report</span>
                                    </div> : 'In progress'}
                                </td>;
                            })}
                            <td data-noprint='true'><DocumentIcon onClick={() => navigateToResults(applicant)} /></td>
                            <td data-noprint='true'><ProfileIcon onClick={() => openExaminerProfile(applicant.userId)} /></td>
                        </tr>)}
                        {statics && applicants.length === 0 && <tr>
                            <td colSpan={COLUMN_COUNT + 1} style={{ textAlign: 'center' }}>No data.</td>
                        </tr>}
                    </tbody>
                </table>
            </div>

            <div className={styles.mobile_list}>
                {applicants.map(applicant => <div className={styles.card} key={applicant.userId}>
                    <div className={styles.card_header} onClick={() => toggleRow(applicant.userId)}>
                        <span>{applicant.first_name} {applicant.last_name}</span>
                        <ChevronDownIcon style={{ transform: isExpanded(applicant.userId) ? 'rotate(180deg)' : 'none' }} />
                    </div>

                    {isExpanded(applicant.userId) && <div className={styles.card_body}>
                        <div className={styles.card_body_section}>
                            <div className={styles.card_body_section_title}>
                                Criteria
                            </div>
                            <div className={styles.card_body_section_content}>
                                {RECRUITING_INTERVIEW_CRITERIA.map(criteria => {
                                    const matched = criteriaOf(applicant, criteria.key);

                                    return <div key={criteria.key} className={styles.card_body_section_content_item}>
                                        <span>{criteria.title}</span>
                                        <div className={styles.card_body_section_content_item_result}>
                                            <ResultIcon result={matched?.result || 'Not Applicable'} />
                                            {matched && <span onClick={() => openReport(matched)}>Report</span>}
                                        </div>
                                    </div>;
                                })}
                            </div>
                        </div>
                    </div>}
                </div>)}
            </div>
        </div>
    );
});

export default RecruitingInterviewTable;
